<script setup>
import { ref, onBeforeMount, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useLayout } from '@/layout/admin/composables/layout';

const route = useRoute();

const { layoutConfig, layoutState, setActiveMenuItem, onMenuToggle } = useLayout();

const props = defineProps({
    item: {
        type: Object,
        default: () => ({})
    },
    index: {
        type: Number,
        default: 0
    },
    root: {
        type: Boolean,
        default: true
    },
    parentItemKey: {
        type: String,
        default: null
    }
});

const isActiveMenu = ref(false);
const itemKey = ref(null);

onBeforeMount(() => {
    itemKey.value = props.parentItemKey ? props.parentItemKey + '-' + props.index : String(props.index);

    const activeItem = layoutState.activeMenuItem;

    isActiveMenu.value = activeItem === itemKey.value || activeItem ? activeItem.startsWith(itemKey.value + '-') : false;
});

watch(
    () => layoutConfig.activeMenuItem.value,
    (newVal) => {
        isActiveMenu.value = newVal === itemKey.value || newVal.startsWith(itemKey.value + '-');
    }
);
const itemClick = (event, item) => {
    if (item.disabled) {
        event.preventDefault();
        return;
    }

    const { overlayMenuActive, staticMenuMobileActive } = layoutState;

    if ((item.to || item.url) && (staticMenuMobileActive.value || overlayMenuActive.value)) {
        onMenuToggle();
    }

    if (item.command) {
        item.command({ originalEvent: event, item: item });
    }

    const foundItemKey = item.items ? (isActiveMenu.value ? props.parentItemKey : itemKey) : itemKey.value;

    setActiveMenuItem(foundItemKey);
};

const checkActiveRoute = (item) => {
    return route.path === item.to;
};
</script>

<template>
  <li :class="{ 'layout-root-menuitem': root, 'active-menuitem': isActiveMenu }">
    <a v-if="(!item.to || item.items) && item.visible !== false"
      :href="item.url"
      @click="itemClick($event, item, index)"
      :class="['menu-item', item.class]"
      :target="item.target"
      tabindex="0">
      <i :class="[item.icon, 'layout-menuitem-icon']"></i>
      <span class="layout-menuitem-text">{{ item.label }}</span>
      <i v-if="item.items" class="pi pi-fw pi-angle-down layout-submenu-toggler"></i>
    </a>

    <router-link
      v-if="item.to && !item.items && item.visible !== false"
      @click="itemClick($event, item, index)"
      :class="['menu-item', item.class, { 'active-route': checkActiveRoute(item) }]"
      tabindex="0"
      :to="item.to">
      <i :class="[item.icon, 'layout-menuitem-icon']"></i>
      <span class="layout-menuitem-text">{{ item.label }}</span>
    </router-link>

    <Transition v-if="item.items && item.visible !== false" name="layout-submenu">
      <ul v-show="root ? true : isActiveMenu" class="layout-submenu">
        <app-menu-item
          v-for="(child, i) in item.items"
          :key="child"
          :index="i"
          :item="child"
          :parentItemKey="itemKey"
          :root="false">
        </app-menu-item>
      </ul>
    </Transition>
  </li>
</template>

<style lang="scss" scoped>
.menu-item {
  @apply flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-gray-100;

  &:hover {
    @apply bg-white/10;
  }

  &.active-route {
    @apply bg-white text-[#0960A5];
  }

  .layout-menuitem-icon {
    @apply text-lg;
  }

  .layout-menuitem-text {
    @apply text-sm;
  }
}

.layout-submenu {
  @apply mt-2 space-y-2 pl-3;
}

.layout-submenu-toggler {
  @apply ml-auto;
}

// Transition styles
.layout-submenu-enter-active,
.layout-submenu-leave-active {
  transition: all 0.3s ease;
}

.layout-submenu-enter-from,
.layout-submenu-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.layout-menu ul a.active-route {
    background-color: var(--surface-ground, white);
}

.layout-menu ul a {
    border-radius: 5px;
    color: white;
}

.layout-menu ul a:hover {
    border-radius: 5px;
    color: #0663CA;
    background-color: var(--surface-ground, black);
}
</style>
